export interface CabecalhoCsv {
    chave: string;
    rotulo: string;
    // Tipo opcional para formatação numérica no CSV (pt-BR, sem notação científica).
    // Colunas sem `tipo` mantêm o comportamento antigo (não quebra os demais relatórios).
    tipo?: 'moeda' | 'percentual' | 'inteiro';
}

export interface DadosCsv {
    cabecalho: CabecalhoCsv[];
    dados: any[];
    nome: string;
}

// Tabela de relatório com o título exibido na tela: vira um CSV próprio ou uma seção do CSV completo.
export interface TabelaCsv extends DadosCsv {
    titulo: string;
}

export class CsvUtils {

    static baixar(dadosCsv: DadosCsv, ano: string, localizacaoSelecionada?: string): void {
        this.baixarConteudo(this.obterConteudoCsv(dadosCsv), dadosCsv.nome, ano, localizacaoSelecionada);
    }

    // Várias tabelas num único arquivo: título na 1ª coluna e a tabela logo abaixo, a partir da 2ª coluna.
    static baixarTabelas(tabelas: TabelaCsv[], nome: string, ano: string, localizacaoSelecionada?: string): void {
        this.baixarConteudo(this.gerarConteudoTabelas(tabelas), nome, ano, localizacaoSelecionada);
    }

    static gerarConteudoTabelas(tabelas: TabelaCsv[]): string {
        const linhas: string[][] = [];

        tabelas.forEach((tabela, indice) => {
            if (indice > 0) linhas.push([]);
            linhas.push([tabela.titulo]);
            linhas.push(['', ...tabela.cabecalho.map(cabecalho => cabecalho.rotulo)]);
            tabela.dados.forEach(linhaDados =>
                linhas.push(['', ...tabela.cabecalho.map(cabecalho => this.formatarCelula(linhaDados[cabecalho.chave], cabecalho.tipo))])
            );
        });

        return linhas.map(linha => linha.join(';')).join('\n');
    }

    private static baixarConteudo(conteudoCsv: string, nome: string, ano: string, localizacaoSelecionada?: string): void {
        const BOM: string = '﻿';
        const nomeArquivo: string = this.obterNomeArquivo(nome, localizacaoSelecionada, ano);
        const dadosArquivo: string = `${BOM}${conteudoCsv}`;
        const blob: Blob = new Blob([dadosArquivo], { type: 'text/csv;charset=utf-8' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = nomeArquivo;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    static gerarConteudoCsv(dadosCsv: DadosCsv): string {
        return this.obterConteudoCsv(dadosCsv);
    }

    private static obterNomeArquivo(nomeOriginal: string, localizacaoSelecionada?: string, ano?: string): string {
        const extensaoArquivoCsv: string = '.csv';
        let nomeArquivo: string = nomeOriginal;

        if (nomeArquivo.indexOf(extensaoArquivoCsv) === -1) {
            if (localizacaoSelecionada && ano) {
                nomeArquivo += ` - ${localizacaoSelecionada} [${ano}]${extensaoArquivoCsv}`;
            } else {
                nomeArquivo += extensaoArquivoCsv;
            }
        }

        return nomeArquivo;
    }

    // Formata número no padrão pt-BR (separador de milhar e vírgula decimal), sem notação científica.
    private static formatarNumero(valor: number, tipo: 'moeda' | 'percentual' | 'inteiro'): string {
        // Sem valor numérico válido (ex.: divisão por zero -> NaN/Infinity) -> célula vazia, igual à tela.
        if (!Number.isFinite(valor)) return '';
        const casas = tipo === 'percentual' ? 1 : tipo === 'moeda' ? 2 : 0;
        return new Intl.NumberFormat('pt-BR', {
            minimumFractionDigits: casas,
            maximumFractionDigits: casas,
        }).format(valor);
    }

    private static formatarCelula(bruto: unknown, tipo?: CabecalhoCsv['tipo']): string {
        if (bruto === undefined || bruto === null) return '';
        if (typeof bruto !== 'number') return String(bruto);
        return tipo ? this.formatarNumero(bruto, tipo) : String(bruto).replace('.', ',');
    }

    private static obterConteudoCsv(dadosCsv: DadosCsv): string {
        const separador: string = ';';
        const quebraLinha: string = '\n';
        const linhasCsv: string[] = [dadosCsv.cabecalho.map(cabecalho => cabecalho.rotulo).join(separador)];

        for (const linhaDados of dadosCsv.dados) {
            linhasCsv.push(
                dadosCsv.cabecalho.map(cabecalho => this.formatarCelula(linhaDados[cabecalho.chave], cabecalho.tipo)).join(separador)
            );
        }

        return linhasCsv.join(quebraLinha);
    }
}
